import { Inject, Injectable, LOCALE_ID, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { CurrencySetting } from '@us/web/shared/data-access';
import { selectCurrencySetting } from '../+state/shop.reducer';
import { isNumber } from 'lodash';
import { CHAR_HYPHEN, TuiRounding, tuiRoundWith } from '@taiga-ui/cdk';
import { tuiGetFractionPartPadded } from '@taiga-ui/core';

const isFloatWithDecimal = (num: number): boolean => num !== Math.floor(num);

const DEFAULT_CURRENCY_SETTING = {
    decimal: 'not-zero' as 'always' | 'never' | 'not-zero',
    number_of_decimal_places: 2,
    decimal_separator: '.',
    thousands_separator: ',',
    display_reduce_price: false,
    display_currency_unit: false,
    currency_unit: '',
    display_decimal: false,
    currency_decimal: 'not-zero' as 'always' | 'never' | 'not-zero',
    currency_number_of_decimal_places: 2
} as const;

@Injectable({
    providedIn: 'root'
})
export class CurrencyService {
    private readonly store = inject(Store);

    private readonly currencySetting = this.store.selectSignal(selectCurrencySetting);

    formatNumber(value: number | string | null | undefined, currencySetting: CurrencySetting | null = null) {
        return this.format(value, currencySetting, false);
    }

    formatCurrency(value: number | string | null | undefined, currencySetting: CurrencySetting | null = null) {
        return this.format(value, currencySetting, true);
    }

    private format(
        value: number | string | null | undefined,
        currencySetting: CurrencySetting | null = null,
        isCurrency = true
    ): string {
        if (!isNumber(value)) {
            return value || '';
        }

        const config = {
            ...DEFAULT_CURRENCY_SETTING,
            ...this.currencySetting(),
            ...currencySetting
        };

        const decimal = isCurrency ? config.currency_decimal : config.decimal;

        const decimalLimit =
            decimal === 'never'
                ? 0
                : Number(
                      (isCurrency ? config.currency_number_of_decimal_places : config.number_of_decimal_places) || 0
                  ) || 0;
        const decimalSeparator = config.decimal_separator;
        const thousandSeparator = config.thousands_separator;
        const rounding: TuiRounding = 'round';

        const rounded = Number.isFinite(decimalLimit)
            ? tuiRoundWith({ value, precision: decimalLimit, method: rounding })
            : value;

        const integerPartString = String(Math.floor(Math.abs(rounded)));
        let fractionPartPadded = tuiGetFractionPartPadded(rounded, decimalLimit);

        if (Number.isFinite(decimalLimit)) {
            if (
                decimal !== 'never' &&
                (decimal === 'always' || (isFloatWithDecimal(value) && config.decimal === 'not-zero'))
            ) {
                const zeroPaddingSize: number = Math.max(decimalLimit - fractionPartPadded.length, 0);
                const zeroPartString = `0`.repeat(zeroPaddingSize);

                fractionPartPadded = `${fractionPartPadded}${zeroPartString}`;
            } else {
                fractionPartPadded = '';
                // fractionPartPadded = fractionPartPadded.replace(/0*$/, ``);
            }
        }

        const remainder = integerPartString.length % 3;
        const sign = value < 0 ? CHAR_HYPHEN : ``;
        let result = sign + integerPartString.charAt(0);

        for (let i = 1; i < integerPartString.length; i++) {
            if (i % 3 === remainder && integerPartString.length > 3) {
                result += thousandSeparator;
            }

            result += integerPartString.charAt(i);
        }

        const currencyUnit = isCurrency && config.display_currency_unit ? config.currency_unit || '' : '';
        return (fractionPartPadded ? result + decimalSeparator + fractionPartPadded : result) + currencyUnit;
    }
}
